import type { Theme } from '@mui/material/styles';
import { varAlpha } from '../theme/styles';
import { SURFACE } from '@/theme/brand-tokens';

export const bulletColor = {
  dark: SURFACE.dark.active,
  light: SURFACE.light.active,
};

function colorVars(theme: Theme, variant?: 'vertical' | 'mini' | 'horizontal') {
  const {
    vars: { palette },
  } = theme;

  // Custom active background color - standard varAlpha based on primary color
  const activeBgColor = varAlpha(palette.primary.main, 0.08);
  const activeHoverBgColor = varAlpha(palette.primary.main, 0.16);
  
  return {
    '--nav-item-color': palette.text.primary,
    '--nav-item-hover-bg': palette.action.hover,
    '--nav-item-caption-color': palette.text.disabled,
    // root
    '--nav-item-root-active-color': palette.primary.main,
    '--nav-item-root-active-color-on-dark': palette.primary.light,
    '--nav-item-root-active-bg': activeBgColor,
    '--nav-item-root-active-hover-bg': activeHoverBgColor,
    '--nav-item-root-open-color': palette.text.primary,
    '--nav-item-root-open-bg': palette.action.hover,
    // sub
    '--nav-item-sub-active-color': palette.text.primary,
    '--nav-item-sub-active-bg': activeBgColor,
    '--nav-item-sub-open-color': palette.text.primary,
    '--nav-item-sub-open-bg': palette.action.hover,
    // icon colors
    '--nav-icon-active-color': palette.primary.main,
    '--nav-icon-open-color': palette.text.primary,
    ...(variant === 'vertical' && {
      '--nav-item-sub-active-bg': activeBgColor,
      '--nav-subheader-color': palette.text.disabled,
      '--nav-subheader-hover-color': palette.text.primary,
    }),
  };
}

// ----------------------------------------------------------------------

function verticalVars(theme: Theme) {
  const { shape, spacing } = theme;

  return {
    ...colorVars(theme, 'vertical'),
    '--nav-item-gap': spacing(0.25),
    '--nav-item-radius': `${shape.borderRadius}px`,
    '--nav-item-pt': spacing(0.25),
    '--nav-item-pr': spacing(0.5),
    '--nav-item-pb': spacing(0.25),
    '--nav-item-pl': spacing(0.75),
    // root
    '--nav-item-root-height': '32px',
    // sub
    '--nav-item-sub-height': '28px',
    // icon
    '--nav-icon-size': '14px',
    '--nav-icon-margin': spacing(0, 0.75, 0, 0),
    // bullet
    '--nav-bullet-size': '12px',
    '--nav-bullet-light-color': bulletColor.light,
    '--nav-bullet-dark-color': bulletColor.dark,
  };
}

// ----------------------------------------------------------------------

function miniVars(theme: Theme) {
  const { shape, spacing } = theme;

  return {
    ...colorVars(theme, 'mini'),
    '--nav-item-gap': spacing(0.25),
    '--nav-item-radius': `${shape.borderRadius}px`,
    // root
    '--nav-item-root-height': '40px',
    '--nav-item-root-padding': spacing(0.5, 0.25, 0.5, 0.25),
    '--nav-item-root-width': '100%',
    '--nav-item-root-max-width': '100%',
    // sub
    '--nav-item-sub-height': '28px',
    '--nav-item-sub-padding': spacing(0, 0.5),
    // icon
    '--nav-icon-size': '16px',
    '--nav-icon-root-margin': spacing(0, 0, 0.5, 0),
    '--nav-icon-sub-margin': spacing(0, 0.5, 0, 0),
  };
}

// ----------------------------------------------------------------------

function horizontalVars(theme: Theme) {
  const { shape, spacing } = theme;

  return {
    ...colorVars(theme, 'horizontal'),
    '--nav-item-gap': spacing(0.75),
    '--nav-height': '56px',
    '--nav-item-radius': `${shape.borderRadius * 0.75}px`,
    // root
    '--nav-item-root-height': '32px',
    '--nav-item-root-padding': spacing(0, 0.75),
    // sub
    '--nav-item-sub-height': '34px',
    '--nav-item-sub-padding': spacing(0, 1),
    // icon
    '--nav-icon-size': '22px',
    '--nav-icon-sub-margin': spacing(0, 1, 0, 0),
    '--nav-icon-root-margin': spacing(0, 1, 0, 0),
  };
}

// ----------------------------------------------------------------------

export const navSectionCssVars = {
  mini: miniVars,
  vertical: verticalVars,
  horizontal: horizontalVars,
};
